import { Component, ErrorInfo, ReactNode } from 'react';
import { datadogRum } from '@datadog/browser-rum';

type Props = {
  children?: ReactNode;
  resetKey?: string | number;
  context?: Record<string, unknown>;
};

type State = { hasError: boolean };

export class ProductDetailPageErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    const { resetKey, context } = this.props;

    const attrs = {
      'pdp.section': 'pdp',
      'pdp.resetKey': String(resetKey ?? ''),
      'pdp.componentStack': info?.componentStack,
      ...Object.fromEntries(
        Object.entries(context ?? {}).map(([key, value]) => [`pdp.${key}`, value])
      )
    };

    try {
      datadogRum.addError(error as Error, attrs);
    } catch {
      // ignore
    }
  }

  componentDidUpdate(prevProps: Props) {
    // reset on navigation change
    if (this.props.resetKey !== prevProps.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      // render nothing on error to keep page stable
      return null;
    }
    return this.props.children;
  }
}
